/**
 * PDF ダウンロード型資料 ビューワー画面 (v3.2.28+)。
 *
 * - 左ペイン: 属性編集 + リンクリスト (新規発行 / 期限更新 / 削除)
 * - 右ペイン: PDF.js ビューワー (ズーム / スクロール)
 */
import type { DocumentStatus, DocumentThankYouPage } from '@goenhub/shared';
import {
  ArrowLeft,
  Copy,
  Heart,
  Image as ImageIcon,
  Info,
  Link2,
  Loader2,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Trash2,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ImageGalleryDialog } from '@/components/common/ImageGalleryDialog';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { storage } from '@/lib/firebase';
import {
  useDeleteDocument,
  useDocument,
  useExtendPdfDownloadLink,
  useIssuePdfDownloadLink,
  usePdfDownloadLinks,
  useRevokePdfDownloadLink,
  useUpdatePdfDocumentMeta,
  useUploadPdfDocumentFile,
} from '@/hooks/useDocuments';
import { paths } from '@/routes/paths';

import { getDownloadURL, ref as storageRef } from 'firebase/storage';

// react-pdf
import { Document as PdfDoc, Page as PdfPage, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
// Vite で worker をバンドル
// eslint-disable-next-line import/no-unresolved
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const STATUS_LABEL: Record<DocumentStatus, string> = {
  draft: '下書き',
  published: '公開中',
  archived: 'アーカイブ',
};

const STATUS_BADGE_VARIANT: Record<
  DocumentStatus,
  'accent' | 'draft' | 'default'
> = {
  draft: 'draft',
  published: 'accent',
  archived: 'default',
};

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch {
    return iso;
  }
}

export function PdfDocumentViewerPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useDocument(id);
  const updateMeta = useUpdatePdfDocumentMeta();
  const uploadFile = useUploadPdfDocumentFile();
  const deleteMutation = useDeleteDocument();
  const linksQuery = usePdfDownloadLinks(id);
  const issueLink = useIssuePdfDownloadLink();
  const revokeLink = useRevokePdfDownloadLink(id);
  const extendLink = useExtendPdfDownloadLink(id);

  // 編集用 state
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [status, setStatus] = useState<DocumentStatus>('draft');
  const [tokenValidityHours, setTokenValidityHours] = useState<number>(168);
  const [validityMode, setValidityMode] = useState<'preset' | 'unlimited'>('preset');
  // v3.2.28+: SA ポータル表示可否 (既定: true)
  const [visibleToSa, setVisibleToSa] = useState<boolean>(true);
  // メインタブ (基本情報 / リンク管理 / 感謝ページ)
  const [mainTab, setMainTab] = useState<'info' | 'links' | 'thanks'>('info');

  // 感謝ページの編集用 state (空文字は「既定値を使う」を意味する)
  const [typHeadline, setTypHeadline] = useState('');
  const [typSubtitle, setTypSubtitle] = useState('');
  const [typNote, setTypNote] = useState('');
  const [typButtonLabel, setTypButtonLabel] = useState('');
  const [typFooterText, setTypFooterText] = useState('');
  const [typLogoUrl, setTypLogoUrl] = useState('');
  const [logoGalleryOpen, setLogoGalleryOpen] = useState(false);

  // 既定値 (Function 側と揃える)
  const TYP_DEFAULTS = {
    headline: '資料のダウンロードを開始します',
    subtitle: 'ご請求いただきありがとうございました。',
    note: '自動でダウンロードが始まらない場合は、下のボタンをクリックしてください。',
    buttonLabel: 'ダウンロード',
    footerText: 'caudesign — GoEnHub',
  };

  // PDF preview state
  const [pdfDataUrl, setPdfDataUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  // displayZoom: ユーザー操作で即時更新される視覚倍率 (CSS transform で適用)
  // renderZoom : 実際に PdfPage を再描画する倍率。 displayZoom を 200ms debounce で追従
  // これにより、ズーム操作中は既存描画を CSS で拡縮表示し続け、ブランクフラッシュを回避
  const [displayZoom, setDisplayZoom] = useState<number>(1.0);
  const [renderZoom, setRenderZoom] = useState<number>(1.0);
  const [containerWidth, setContainerWidth] = useState<number>(800);
  const pdfContainerRef = useRef<HTMLDivElement>(null);

  // 差し替えアップロード
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const [replaceProgress, setReplaceProgress] = useState<number | null>(null);

  // リンク発行 input
  const [issueEmail, setIssueEmail] = useState('');
  const [issueValidity, setIssueValidity] = useState<number | ''>('');

  useEffect(() => {
    if (data) {
      setTitle(data.title);
      setSlug(data.slug);
      setStatus(data.status);
      if (data.tokenValidityHours === null || data.tokenValidityHours === undefined) {
        setValidityMode('unlimited');
        setTokenValidityHours(168);
      } else {
        setValidityMode('preset');
        setTokenValidityHours(data.tokenValidityHours);
      }
      setVisibleToSa(data.visibleToSa !== false);
      const typ = data.thankYouPage ?? {};
      setTypHeadline(typ.headline ?? '');
      setTypSubtitle(typ.subtitle ?? '');
      setTypNote(typ.note ?? '');
      setTypButtonLabel(typ.buttonLabel ?? '');
      setTypFooterText(typ.footerText ?? '');
      setTypLogoUrl(typ.logoUrl ?? '');
    }
  }, [data]);

  // 管理画面用に PDF を直接取得 (Storage rules で admin 読取可)
  useEffect(() => {
    if (!data?.pdfFile?.storagePath) {
      setPdfDataUrl(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const url = await getDownloadURL(storageRef(storage, data.pdfFile!.storagePath));
        if (!cancelled) setPdfDataUrl(url);
      } catch (e) {
        if (!cancelled) {
          toast.error(
            `PDF の取得に失敗: ${e instanceof Error ? e.message : String(e)}`,
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data?.pdfFile?.storagePath]);

  useEffect(() => {
    const el = pdfContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 800;
      setContainerWidth(Math.max(320, w - 16));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // displayZoom が変わったら 200ms 後に renderZoom も追従させて高解像度で再描画
  useEffect(() => {
    if (displayZoom === renderZoom) return;
    const t = setTimeout(() => setRenderZoom(displayZoom), 200);
    return () => clearTimeout(t);
  }, [displayZoom, renderZoom]);

  const handleSave = async () => {
    if (!id) return;
    try {
      const thankYouPage: DocumentThankYouPage = {
        headline: typHeadline.trim() || null,
        subtitle: typSubtitle.trim() || null,
        note: typNote.trim() || null,
        buttonLabel: typButtonLabel.trim() || null,
        footerText: typFooterText.trim() || null,
        logoUrl: typLogoUrl.trim() || null,
      };
      const allEmpty = Object.values(thankYouPage).every((v) => !v);
      await updateMeta.mutateAsync({
        id,
        title: title.trim(),
        slug: slug.trim() || undefined,
        status,
        tokenValidityHours:
          validityMode === 'unlimited' ? null : Math.max(1, Math.floor(tokenValidityHours)),
        thankYouPage: allEmpty ? null : thankYouPage,
        visibleToSa,
      });
      toast.success('保存しました');
    } catch (e) {
      toast.error(`保存失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  /**
   * 資料の完全削除。カスケード対象:
   *   - Firestore: documents/{id} + 全 document_access_tokens
   *   - Storage:   documents/files/{id}/*.pdf (deleteDocument Function 側)
   * 削除後は一覧に戻る。
   */
  const handleDelete = async () => {
    if (!id || !data) return;
    const fileInfo = data.pdfFile
      ? `\n・PDF ファイル (${data.pdfFile.fileName}) を Storage から削除`
      : '';
    const tokensCount = linksQuery.data?.length ?? 0;
    const tokenInfo =
      tokensCount > 0
        ? `\n・発行済みダウンロードリンク ${tokensCount} 件を全て削除`
        : '';
    if (
      !confirm(
        `資料「${data.title}」を完全に削除します。この操作は取り消せません。\n${fileInfo}${tokenInfo}\n\n続行しますか？`,
      )
    )
      return;
    try {
      await deleteMutation.mutateAsync(id);
      toast.success(`「${data.title}」を削除しました`);
      navigate(paths.documents);
    } catch (e) {
      toast.error(
        `削除失敗: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
  };

  const resetTypField = (
    field: 'headline' | 'subtitle' | 'note' | 'buttonLabel' | 'footerText',
  ) => {
    switch (field) {
      case 'headline': setTypHeadline(''); break;
      case 'subtitle': setTypSubtitle(''); break;
      case 'note': setTypNote(''); break;
      case 'buttonLabel': setTypButtonLabel(''); break;
      case 'footerText': setTypFooterText(''); break;
    }
  };

  const handleReplaceFile = async (file: File) => {
    if (!id) return;
    try {
      setReplaceProgress(0);
      await uploadFile.mutateAsync({
        documentId: id,
        file,
        onProgress: (p) => setReplaceProgress(p),
      });
      toast.success('PDF を差し替えました');
    } catch (e) {
      toast.error(`差し替え失敗: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setReplaceProgress(null);
    }
  };

  const handleIssueLink = async () => {
    if (!id) return;
    try {
      const r = await issueLink.mutateAsync({
        documentId: id,
        email: issueEmail.trim() || null,
        validityHours:
          issueValidity === '' ? null : Math.max(1, Math.floor(Number(issueValidity))),
      });
      try {
        await navigator.clipboard.writeText(r.url);
        toast.success('発行しました (URL をクリップボードにコピー済み)');
      } catch {
        toast.success('発行しました');
      }
      setIssueEmail('');
      setIssueValidity('');
    } catch (e) {
      toast.error(`発行失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const links = useMemo(() => linksQuery.data ?? [], [linksQuery.data]);
  const activeLinks = useMemo(() => links.filter((l) => !l.expired), [links]);
  const expiredLinks = useMemo(() => links.filter((l) => l.expired), [links]);

  if (isLoading) {
    return (
      <Screen title="PDF 資料">
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      </Screen>
    );
  }
  if (!data) {
    return (
      <Screen title="PDF 資料">
        <p className="text-[var(--text-muted)] text-center py-16">
          資料が見つかりません。
          <button
            type="button"
            className="ml-2 text-[var(--accent)] underline"
            onClick={() => navigate(paths.documents)}
          >
            一覧に戻る
          </button>
        </p>
      </Screen>
    );
  }
  if (data.type !== 'pdf_download') {
    return (
      <Screen title="PDF 資料">
        <p className="text-[var(--text-muted)] text-center py-16">
          この資料は PDF ダウンロード型ではありません。
        </p>
      </Screen>
    );
  }

  return (
    <Screen
      title={data.title}
      description={`/dl/${data.slug}`}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => navigate(paths.documents)}>
            <ArrowLeft className="size-4 mr-1" /> 一覧へ
          </Button>
          <Badge variant={STATUS_BADGE_VARIANT[data.status]}>
            {STATUS_LABEL[data.status]}
          </Badge>
          <Button variant="primary" onClick={() => void handleSave()} disabled={updateMeta.isPending}>
            <Save className="size-4 mr-1" />
            {updateMeta.isPending ? '保存中…' : '保存'}
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-[420px_minmax(0,1fr)] gap-4 lg:items-start">
        {/* 左ペイン: 3 タブ (基本情報 / リンク管理 / 感謝ページ) */}
        <Tabs value={mainTab} onValueChange={(v) => setMainTab(v as typeof mainTab)} className="space-y-3">
          <TabsList>
            <TabsTrigger value="info">
              <Info className="size-3.5 mr-1" />
              基本情報
            </TabsTrigger>
            <TabsTrigger value="links">
              <Link2 className="size-3.5 mr-1" />
              リンク管理
              {links.length > 0 && (
                <span className="ml-1 text-[10px] text-[var(--text-muted)]">
                  {links.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="thanks">
              <Heart className="size-3.5 mr-1" />
              感謝ページ
            </TabsTrigger>
          </TabsList>

          {/* ── 基本情報タブ ── */}
          <TabsContent value="info" className="space-y-4">
          {/* 基本情報 */}
          <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] p-4 space-y-3">
            <h3 className="text-sm font-semibold mb-1">基本情報</h3>
            <Field label="タイトル">
              <Input value={title} onChange={(e) => setTitle(e.target.value)} />
            </Field>
            <Field label="slug (URL パス)">
              <div className="flex items-center gap-1.5">
                <span className="text-[12px] text-[var(--text-muted)]">/dl/</span>
                <Input value={slug} onChange={(e) => setSlug(e.target.value)} />
              </div>
            </Field>
            <Field label="ステータス">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as DocumentStatus)}
                className="w-full rounded-md border border-[var(--border)] bg-[var(--bg-input)] px-2.5 py-1.5 text-[13px]"
              >
                <option value="draft">下書き</option>
                <option value="published" disabled={!data.pdfFile}>
                  公開中{!data.pdfFile ? ' (PDF 未アップロード)' : ''}
                </option>
                <option value="archived">アーカイブ</option>
              </select>
            </Field>
            <div className="pt-2 border-t border-[var(--border)]">
              <label className="flex items-center justify-between gap-3 cursor-pointer select-none">
                <div className="flex-1">
                  <div className="text-[12px] font-medium">
                    SA ポータルに表示
                  </div>
                  <div className="text-[10px] text-[var(--text-muted)] mt-0.5">
                    OFF にすると外部 SA の資料集 (/sa/documents) で非表示になります。
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={visibleToSa}
                  onChange={(e) => setVisibleToSa(e.target.checked)}
                  className="appearance-none relative w-10 h-5 rounded-full bg-[var(--bg-hover)] cursor-pointer transition-colors checked:bg-[var(--accent)] before:content-[''] before:absolute before:left-0.5 before:top-0.5 before:size-4 before:rounded-full before:bg-white before:transition-transform checked:before:translate-x-5"
                />
              </label>
            </div>
            <Field label="リンク既定有効期限">
              <div className="flex items-center gap-2 flex-wrap">
                <label className="inline-flex items-center gap-1 text-[12px]">
                  <input
                    type="radio"
                    name="default-validity"
                    checked={validityMode === 'preset'}
                    onChange={() => setValidityMode('preset')}
                  />
                  時間指定
                </label>
                <label className="inline-flex items-center gap-1 text-[12px]">
                  <input
                    type="radio"
                    name="default-validity"
                    checked={validityMode === 'unlimited'}
                    onChange={() => setValidityMode('unlimited')}
                  />
                  無期限
                </label>
                {validityMode === 'preset' && (
                  <Input
                    type="number"
                    min={1}
                    value={tokenValidityHours}
                    onChange={(e) =>
                      setTokenValidityHours(Number(e.target.value) || 168)
                    }
                    className="w-24"
                  />
                )}
                {validityMode === 'preset' && (
                  <span className="text-[12px] text-[var(--text-muted)]">時間</span>
                )}
              </div>
            </Field>
          </div>

          {/* ファイル情報 + 差し替え */}
          <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] p-4 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">ファイル</h3>
              <button
                type="button"
                onClick={() => replaceInputRef.current?.click()}
                className="text-[11px] text-[var(--accent)] hover:underline inline-flex items-center gap-1"
                disabled={replaceProgress !== null}
              >
                <RefreshCw className="size-3" />
                {replaceProgress !== null ? `差し替え中… ${replaceProgress}%` : '差し替え'}
              </button>
              <input
                ref={replaceInputRef}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleReplaceFile(f);
                  e.target.value = '';
                }}
              />
            </div>
            {data.pdfFile ? (
              <div className="text-[12px] space-y-0.5 text-[var(--text-muted)]">
                <div className="truncate font-mono text-[var(--text-primary)]">
                  {data.pdfFile.fileName}
                </div>
                <div>
                  {(data.pdfFile.size / 1024 / 1024).toFixed(2)} MB
                  {data.pdfFile.pageCount ? ` / ${data.pdfFile.pageCount} ページ` : ''}
                  {data.pdfFile.version > 1 ? ` / v${data.pdfFile.version}` : ''}
                </div>
              </div>
            ) : (
              <p className="text-[12px] text-amber-600">PDF が未アップロードです</p>
            )}
          </div>

          {/* ── 危険な操作 (Danger Zone) ──
              最下部に配置してスクロールが必要 = 意識的なフリクションで誤削除を防ぐ。
              GitHub 等の SaaS 標準パターン。
          */}
          <div className="rounded-lg border border-red-200 bg-red-50/50 dark:border-red-900/40 dark:bg-red-950/20 p-4 space-y-2">
            <h3 className="text-sm font-semibold text-red-700 dark:text-red-400 flex items-center gap-1.5">
              <Trash2 className="size-3.5" />
              危険な操作
            </h3>
            <p className="text-[11px] text-[var(--text-muted)] leading-relaxed">
              この資料を完全に削除します。Firestore レコード・PDF ファイル
              (Storage)・発行済みの全ダウンロードリンクが同時に削除され、
              <span className="font-semibold">取り消しできません</span>。
            </p>
            <button
              type="button"
              onClick={() => void handleDelete()}
              disabled={deleteMutation.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium text-red-700 border border-red-300 bg-white hover:bg-red-100 hover:border-red-400 dark:bg-transparent dark:text-red-400 dark:border-red-900/50 dark:hover:bg-red-950/40 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Trash2 className="size-3.5" />
              )}
              {deleteMutation.isPending ? '削除中…' : 'この資料を削除'}
            </button>
          </div>
          </TabsContent>

          {/* ── リンク管理タブ ── */}
          <TabsContent value="links" className="space-y-4">
          <Tabs defaultValue="issue">
            <TabsList>
              <TabsTrigger value="issue">
                <Plus className="size-3.5 mr-1" /> 新規リンク発行
              </TabsTrigger>
              <TabsTrigger value="list">
                <Link2 className="size-3.5 mr-1" />
                リンク一覧
                <span className="ml-1 text-[10px] text-[var(--text-muted)]">
                  {links.length}
                </span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="issue">
              <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] p-4 space-y-3">
                <p className="text-[11px] text-[var(--text-muted)]">
                  発行されたリンクは即時クリップボードにコピーされます。
                  自動返信メールで送付されるリンクは別途トークンが発行されるため、ここでは管理画面からの手動発行用です。
                </p>
                <Field label="宛先メール (任意、監査用)">
                  <Input
                    value={issueEmail}
                    onChange={(e) => setIssueEmail(e.target.value)}
                    placeholder="example@example.com"
                  />
                </Field>
                <Field label="個別有効時間 (空欄=既定値)">
                  <div className="flex items-center gap-1.5">
                    <Input
                      type="number"
                      min={1}
                      value={issueValidity}
                      onChange={(e) =>
                        setIssueValidity(e.target.value === '' ? '' : Number(e.target.value))
                      }
                      placeholder={
                        data.tokenValidityHours
                          ? `既定: ${data.tokenValidityHours}`
                          : '既定: 無期限'
                      }
                    />
                    <span className="text-[12px] text-[var(--text-muted)]">時間</span>
                  </div>
                </Field>
                <Button
                  onClick={() => void handleIssueLink()}
                  disabled={
                    !data.pdfFile ||
                    data.status !== 'published' ||
                    issueLink.isPending
                  }
                  className="w-full"
                >
                  {issueLink.isPending ? (
                    <Loader2 className="size-4 mr-1 animate-spin" />
                  ) : (
                    <Plus className="size-4 mr-1" />
                  )}
                  ダウンロードリンクを発行
                </Button>
                {(!data.pdfFile || data.status !== 'published') && (
                  <p className="text-[11px] text-amber-600">
                    {!data.pdfFile
                      ? 'PDF をアップロードしてから発行できます'
                      : 'ステータスを「公開中」にしてから発行できます'}
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="list">
              <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] p-3 space-y-2 max-h-[480px] overflow-y-auto">
                {linksQuery.isLoading ? (
                  <div className="flex justify-center py-4">
                    <Spinner />
                  </div>
                ) : links.length === 0 ? (
                  <p className="text-[12px] text-[var(--text-muted)] text-center py-6">
                    まだリンクがありません
                  </p>
                ) : (
                  <>
                    {activeLinks.length > 0 && (
                      <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mt-1 px-1">
                        有効 ({activeLinks.length})
                      </div>
                    )}
                    {activeLinks.map((l) => (
                      <LinkRow
                        key={l.tokenId}
                        link={l}
                        onCopy={async () => {
                          await navigator.clipboard.writeText(l.url);
                          toast.success('URL をコピーしました');
                        }}
                        onRevoke={async () => {
                          if (!confirm('このリンクを削除しますか？')) return;
                          await revokeLink.mutateAsync(l.tokenId);
                          toast.success('リンクを削除しました');
                        }}
                        onExtend={async (hours) => {
                          await extendLink.mutateAsync({
                            tokenId: l.tokenId,
                            validityHours: hours,
                          });
                          toast.success('期限を更新しました');
                        }}
                      />
                    ))}
                    {expiredLinks.length > 0 && (
                      <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mt-3 px-1">
                        期限切れ ({expiredLinks.length})
                      </div>
                    )}
                    {expiredLinks.map((l) => (
                      <LinkRow
                        key={l.tokenId}
                        link={l}
                        onCopy={async () => {
                          await navigator.clipboard.writeText(l.url);
                          toast.success('URL をコピーしました');
                        }}
                        onRevoke={async () => {
                          if (!confirm('このリンクを削除しますか？')) return;
                          await revokeLink.mutateAsync(l.tokenId);
                          toast.success('リンクを削除しました');
                        }}
                        onExtend={async (hours) => {
                          await extendLink.mutateAsync({
                            tokenId: l.tokenId,
                            validityHours: hours,
                          });
                          toast.success('期限を更新しました');
                        }}
                      />
                    ))}
                  </>
                )}
              </div>
            </TabsContent>
          </Tabs>
          </TabsContent>

          {/* ── 感謝ページタブ ── */}
          <TabsContent value="thanks" className="space-y-4">
            <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] p-4 space-y-3">
              <h3 className="text-sm font-semibold flex items-center gap-1.5">
                <Heart className="size-4 text-pink-500" />
                感謝ページのカスタマイズ
              </h3>
              <p className="text-[11px] text-[var(--text-muted)] leading-relaxed">
                ダウンロードリンクをクリックした顧客に表示される
                ページの文言とロゴをカスタマイズできます。
                空欄のままにすると右側に表示された既定文言が使われます。
              </p>
            </div>

            {/* ロゴ画像 */}
            <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] p-4 space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-[12px] font-medium">ロゴ画像 (任意)</label>
                {typLogoUrl && (
                  <button
                    type="button"
                    onClick={() => setTypLogoUrl('')}
                    className="text-[11px] text-[var(--text-muted)] hover:text-red-600 inline-flex items-center gap-1"
                  >
                    <X className="size-3" />
                    クリア
                  </button>
                )}
              </div>
              {typLogoUrl ? (
                <div className="flex items-center gap-3 rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3">
                  <img
                    src={typLogoUrl}
                    alt="ロゴプレビュー"
                    className="size-16 object-contain bg-white rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-[var(--text-muted)] truncate">
                      {typLogoUrl.split('/').pop()?.split('?')[0]}
                    </p>
                    <button
                      type="button"
                      onClick={() => setLogoGalleryOpen(true)}
                      className="text-[11px] text-[var(--accent)] hover:underline mt-1"
                    >
                      別の画像に変更
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setLogoGalleryOpen(true)}
                  className="w-full rounded-md border-2 border-dashed border-[var(--border)] p-4 text-center hover:border-[var(--accent)]/50 hover:bg-[var(--bg-hover)] transition-colors"
                >
                  <ImageIcon className="size-6 mx-auto text-[var(--text-muted)] mb-1" />
                  <span className="text-[12px] text-[var(--text-muted)]">
                    画像ギャラリーから選択
                  </span>
                </button>
              )}
              <p className="text-[10px] text-[var(--text-muted)]">
                ロゴ未設定の場合は緑のチェックアイコン (✓) が表示されます。
              </p>
            </div>

            {/* テキストフィールド群 */}
            <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] p-4 space-y-3">
              <ThankYouField
                label="見出し"
                value={typHeadline}
                placeholder={TYP_DEFAULTS.headline}
                onChange={setTypHeadline}
                onReset={() => resetTypField('headline')}
              />
              <ThankYouField
                label="サブテキスト"
                value={typSubtitle}
                placeholder={TYP_DEFAULTS.subtitle}
                onChange={setTypSubtitle}
                onReset={() => resetTypField('subtitle')}
              />
              <ThankYouField
                label="注意書き (ボタン上)"
                value={typNote}
                placeholder={TYP_DEFAULTS.note}
                onChange={setTypNote}
                onReset={() => resetTypField('note')}
                multiline
              />
              <ThankYouField
                label="ボタンラベル"
                value={typButtonLabel}
                placeholder={TYP_DEFAULTS.buttonLabel}
                onChange={setTypButtonLabel}
                onReset={() => resetTypField('buttonLabel')}
              />
              <ThankYouField
                label="フッターテキスト"
                value={typFooterText}
                placeholder={TYP_DEFAULTS.footerText}
                onChange={setTypFooterText}
                onReset={() => resetTypField('footerText')}
              />
            </div>

            {/* プレビュー */}
            <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] p-4 space-y-2">
              <h3 className="text-[12px] font-medium text-[var(--text-muted)]">
                プレビュー (保存後の表示)
              </h3>
              <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] p-5 text-center space-y-2">
                {typLogoUrl ? (
                  <img
                    src={typLogoUrl}
                    alt=""
                    className="h-12 object-contain mx-auto mb-2"
                  />
                ) : (
                  <div className="size-12 mx-auto rounded-full bg-green-100 text-green-700 inline-flex items-center justify-center text-[20px] font-bold mb-1">
                    ✓
                  </div>
                )}
                <div className="text-sm font-semibold">
                  {typHeadline.trim() || TYP_DEFAULTS.headline}
                </div>
                <div className="text-[11px] text-[var(--text-muted)] whitespace-pre-line">
                  {typSubtitle.trim() || TYP_DEFAULTS.subtitle}
                </div>
                <div className="mt-2 rounded-md border border-[var(--border)] p-2 text-left flex items-center gap-2">
                  <div className="size-7 rounded bg-red-100 text-red-600 text-[10px] font-bold inline-flex items-center justify-center shrink-0">
                    PDF
                  </div>
                  <div className="text-[11px] truncate">
                    {data.pdfFile?.fileName ?? '(ファイル名)'}
                  </div>
                </div>
                <div className="text-[10px] text-[var(--text-muted)] whitespace-pre-line mt-2">
                  {typNote.trim() || TYP_DEFAULTS.note}
                </div>
                <div className="rounded bg-blue-600 text-white text-[12px] py-2 px-3 inline-block w-full mt-1">
                  {typButtonLabel.trim() || TYP_DEFAULTS.buttonLabel}
                </div>
                <div className="text-[10px] text-[var(--text-muted)] mt-2">
                  {typFooterText.trim() || TYP_DEFAULTS.footerText}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <ImageGalleryDialog
          open={logoGalleryOpen}
          onOpenChange={setLogoGalleryOpen}
          onSelect={(url) => {
            setTypLogoUrl(url);
            setLogoGalleryOpen(false);
          }}
          title="感謝ページのロゴを選択"
          hintText="storage/system/imgs から選択。5MB 以下の画像。"
        />

        {/* 右ペイン: PDF ビューワー */}
        {/* lg 以降は sticky + 固定ビューポート高で、PDF はスクロール内に閉じ込める */}
        <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] flex flex-col lg:sticky lg:top-4 lg:h-[calc(100vh-140px)]">
          <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--border)] flex-shrink-0">
            <div className="text-[12px] text-[var(--text-muted)]">
              {numPages > 0 ? `${numPages} ページ` : 'プレビュー'}
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() =>
                  setDisplayZoom((z) => Math.max(0.5, Math.round((z - 0.1) * 10) / 10))
                }
                className="p-1 rounded hover:bg-[var(--bg-hover)]"
                aria-label="縮小"
              >
                <ZoomOut className="size-4" />
              </button>
              <span className="text-[12px] tabular-nums w-12 text-center">
                {Math.round(displayZoom * 100)}%
              </span>
              <button
                type="button"
                onClick={() =>
                  setDisplayZoom((z) => Math.min(3.0, Math.round((z + 0.1) * 10) / 10))
                }
                className="p-1 rounded hover:bg-[var(--bg-hover)]"
                aria-label="拡大"
              >
                <ZoomIn className="size-4" />
              </button>
            </div>
          </div>
          <div
            ref={pdfContainerRef}
            className="flex-1 overflow-auto p-3 bg-[var(--bg-root)] min-h-[480px]"
          >
            {pdfDataUrl ? (
              <PdfDoc
                file={pdfDataUrl}
                onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                onLoadError={(err) =>
                  toast.error(`PDF 読込失敗: ${err.message ?? String(err)}`)
                }
                loading={
                  <div className="flex justify-center py-16">
                    <Spinner />
                  </div>
                }
                /* PdfDoc 直下の div を flex column + items-center にして水平中央に */
                className="flex flex-col items-center"
              >
                {Array.from({ length: numPages }, (_, i) => (
                  <div
                    key={i + 1}
                    className="mb-3 shadow-sm"
                    /* displayZoom と renderZoom の比率で視覚スケール。renderZoom が追従したら scale=1 */
                    style={{
                      transform: `scale(${displayZoom / renderZoom})`,
                      transformOrigin: 'top center',
                      transition: 'transform 140ms ease-out',
                      willChange: 'transform',
                    }}
                  >
                    <PdfPage
                      pageNumber={i + 1}
                      width={containerWidth * renderZoom}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </div>
                ))}
              </PdfDoc>
            ) : (
              <p className="text-center text-[var(--text-muted)] py-16">
                PDF をアップロードするとここにプレビューが表示されます。
              </p>
            )}
          </div>
        </div>
      </div>
    </Screen>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-[11px] font-medium text-[var(--text-muted)]">{label}</label>
      {children}
    </div>
  );
}

interface ThankYouFieldProps {
  label: string;
  value: string;
  placeholder: string;
  onChange: (v: string) => void;
  onReset: () => void;
  multiline?: boolean;
}

function ThankYouField({
  label,
  value,
  placeholder,
  onChange,
  onReset,
  multiline,
}: ThankYouFieldProps) {
  const isCustom = value.trim().length > 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-medium text-[var(--text-muted)]">
          {label}
          {isCustom && (
            <span className="ml-1.5 text-[10px] text-[var(--accent)]">●</span>
          )}
        </label>
        {isCustom && (
          <button
            type="button"
            onClick={onReset}
            className="text-[10px] text-[var(--text-muted)] hover:text-red-600 inline-flex items-center gap-0.5"
            title="既定値に戻す"
          >
            <RotateCcw className="size-2.5" />
            既定に戻す
          </button>
        )}
      </div>
      {multiline ? (
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="text-[12px]"
        />
      ) : (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      )}
    </div>
  );
}

interface LinkRowProps {
  link: {
    tokenId: string;
    email: string;
    issuedAt: string;
    expiresAt: string | null;
    expired: boolean;
    url: string;
  };
  onCopy: () => void;
  onRevoke: () => void;
  onExtend: (hours: number | null) => void;
}

function LinkRow({ link, onCopy, onRevoke, onExtend }: LinkRowProps) {
  const [extending, setExtending] = useState(false);
  const [extendHours, setExtendHours] = useState<number | ''>('');

  return (
    <div
      className={`rounded-md border p-2.5 text-[11px] space-y-1.5 ${
        link.expired
          ? 'border-[var(--border)] opacity-70'
          : 'border-[var(--border)]'
      }`}
    >
      <div className="flex items-center justify-between gap-1">
        <div className="font-mono truncate flex-1">{link.tokenId.slice(0, 16)}…</div>
        {link.expired ? (
          <Badge variant="default">期限切れ</Badge>
        ) : (
          <Badge variant="accent">有効</Badge>
        )}
      </div>
      {link.email && (
        <div className="text-[var(--text-muted)] truncate">→ {link.email}</div>
      )}
      <div className="text-[10px] text-[var(--text-muted)] flex items-center gap-2 flex-wrap">
        <span>発行 {formatDateTime(link.issuedAt)}</span>
        <span>
          {link.expiresAt ? `期限 ${formatDateTime(link.expiresAt)}` : '無期限'}
        </span>
      </div>
      <div className="flex items-center gap-1 pt-1">
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] hover:bg-[var(--bg-hover)]"
        >
          <Copy className="size-3" />
          URL コピー
        </button>
        <button
          type="button"
          onClick={() => setExtending((v) => !v)}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] hover:bg-[var(--bg-hover)]"
        >
          <RefreshCw className="size-3" />
          期限更新
        </button>
        <button
          type="button"
          onClick={onRevoke}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] text-red-600 hover:bg-red-50"
        >
          <Trash2 className="size-3" />
          削除
        </button>
      </div>
      {extending && (
        <div className="flex items-center gap-1.5 pt-1 border-t border-[var(--border)]">
          <Input
            type="number"
            min={1}
            value={extendHours}
            onChange={(e) =>
              setExtendHours(e.target.value === '' ? '' : Number(e.target.value))
            }
            placeholder="新しい有効時間"
            className="h-7 text-[11px]"
          />
          <button
            type="button"
            onClick={() => {
              onExtend(extendHours === '' ? null : Math.max(1, Math.floor(Number(extendHours))));
              setExtending(false);
              setExtendHours('');
            }}
            className="px-2 py-1 rounded bg-[var(--accent)] text-white text-[10px]"
          >
            適用
          </button>
          <button
            type="button"
            onClick={() => {
              onExtend(null);
              setExtending(false);
              setExtendHours('');
            }}
            className="px-2 py-1 rounded border border-[var(--border)] text-[10px]"
          >
            無期限化
          </button>
        </div>
      )}
    </div>
  );
}
